/**
 * Post-call rating screen — RN counterpart of the web widget's
 * `feedback-screen.tsx`.
 *
 * Two variants, the same two web has, chosen the same way. With messages worth
 * showing, the ended conversation stays readable and the rating hangs off the
 * bottom of it — tapping a star submits straight away, since there is no
 * comment box in that layout. With nothing to replay it falls back to the
 * centred card: title, star row, comment box, submit and skip.
 *
 * The replay is READ-ONLY. Components that were interactive during the call
 * render disabled, and their actions go nowhere: the session is over, so an
 * enabled control would be a promise the widget cannot keep.
 *
 * No conversation state lives here — the rating and the comment are the same
 * two pieces of draft UI state the web source keeps locally, and both leave
 * through `onSubmit` / `onSkip`.
 */

import { useMemo, useRef, useState, type ComponentRef, type FC } from "react";
import { Animated, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import Svg, { Path } from "react-native-svg";
import type { ChatMessage, MediaType, PortableStyles, RinggWidgetConfig } from "@ringg/core";

import { GradientFill, solidColor } from "../lib/gradient";
import { toViewStyle } from "../lib/portable-styles";
import { ringgId } from "../lib/testing";
import { resolveButtonRadius, resolveFontFamily, resolveRadius, useWidgetTheme } from "../lib/widget-theme";
import { Touchable } from "./ui/pressable";
import Header from "./header";
import InteractiveFlow from "./interactive-flow";
import MessageItem from "./message-item";
import BlocksStep from "./steps/blocks-step";
import { isBlocks, type ComponentActionResponse, type InteractiveFlowPayload, type SimpleComponentPayload } from "../lib/component-types";

interface FeedbackScreenProps {
  callMode: MediaType;
  isSubmitting: boolean;
  feedbackScreenConfig?: RinggWidgetConfig["feedbackScreen"];
  /** The ended conversation, replayed above the rating when it has content. */
  messages?: readonly ChatMessage[];
  title?: string;
  logoUrl?: string;
  logoStyles?: PortableStyles;
  onSubmit: (rating: number, comment: string) => void;
  onSkip: () => void;
}

interface StarIconProps {
  filled: boolean;
  filledColor: string;
  emptyColor: string;
  size?: number;
  accessibilityLabel: string;
  onPress: () => void;
}

/**
 * Hardcoded on web too: conventional amber and gray-300, NOT the theme primary
 * — a black brand primary made the stars read as ink blots.
 */
const DEFAULT_STAR_FILLED_COLOR = "#F5A623";
const DEFAULT_STAR_EMPTY_COLOR = "#D1D5DB";

/** Tailwind preflight's `::placeholder` colour (gray-400), which web inherits. */
const PLACEHOLDER_COLOR = "#9CA3AF";

const DEFAULT_STARS_COUNT = 5;

/** `w-8 h-8` on the card variant. */
const STAR_SIZE = 32;

const COMMENT_MAX_LENGTH = 500;

/** Card radius, not button radius — a pill-shaped comment box reads badly. */
const COMMENT_RADIUS_FALLBACK = 16;

const SUBMIT_HEIGHT = 44;

/**
 * The web screen's own star, path data verbatim — a rounded 24-viewBox glyph
 * that is NOT lucide's `Star`. Using lucide here looked close enough in
 * isolation and visibly wrong beside the web and Flutter widgets, which both
 * draw this outline.
 */
const STAR_PATH = "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z";
const STAR_VIEW_BOX = 24;
const STAR_STROKE_WIDTH = 1.5;

/** `hover:scale-110` on web; the same growth, driven by press on touch. */
const STAR_PRESS_SCALE = 1.1;
const STAR_PRESS_SPRING = { stiffness: 400, damping: 22, mass: 1 };

const StarIcon: FC<StarIconProps> = ({ filled, filledColor, emptyColor, size = STAR_SIZE, accessibilityLabel, onPress }) => {
  const scale = useRef(new Animated.Value(1)).current;

  const springTo = (toValue: number): void => {
    Animated.spring(scale, { toValue, ...STAR_PRESS_SPRING, useNativeDriver: true }).start();
  };

  return (
    <Touchable
      onPress={onPress}
      onPressIn={() => springTo(STAR_PRESS_SCALE)}
      onPressOut={() => springTo(1)}
      testID={ringgId("feedback-star")}
      accessibilityLabel={accessibilityLabel}
      style={styles.star}
    >
      <Animated.View style={{ transform: [{ scale }] }}>
        <Svg width={size} height={size} viewBox={`0 0 ${STAR_VIEW_BOX} ${STAR_VIEW_BOX}`}>
          <Path
            d={STAR_PATH}
            fill={filled ? filledColor : "none"}
            stroke={filled ? filledColor : emptyColor}
            strokeWidth={STAR_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </Animated.View>
    </Touchable>
  );
};

/** The replayed flow still needs an api prop; nothing may actually call out. */
const noopApi = async (): Promise<ComponentActionResponse> => ({ success: true, message: "" });

/** Web's `w-7 h-7` on the transcript variant, against `w-8 h-8` on the card. */
const TRANSCRIPT_STAR_SIZE = 28;

const FeedbackScreen: FC<FeedbackScreenProps> = ({ callMode, isSubmitting, feedbackScreenConfig, messages, title, logoUrl, logoStyles, onSubmit, onSkip }) => {
  const theme = useWidgetTheme();
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  // CSS draws `focus:ring-2` for free; RN has no state selectors, so the field
  // has to know it is focused to tint its border.
  const [isCommentFocused, setIsCommentFocused] = useState(false);

  const starsCount = feedbackScreenConfig?.starsCount ?? DEFAULT_STARS_COUNT;
  const stars = Array.from({ length: starsCount }, (_, index) => index + 1);
  const starFilledColor = feedbackScreenConfig?.starsStyles?.filledColor ?? DEFAULT_STAR_FILLED_COLOR;
  const starEmptyColor = feedbackScreenConfig?.starsStyles?.emptyColor ?? DEFAULT_STAR_EMPTY_COLOR;

  const isSubmitDisabled = rating === 0 || isSubmitting;
  const submitOverrides = toViewStyle(feedbackScreenConfig?.submitBtnStyles);
  // Web spreads `submitBtnStyles` over the themed background, so a configured
  // colour wins. Here it has to win as the FILL as well: a gradient primary
  // paints above `backgroundColor`, and would bury the override.
  const submitFill = typeof submitOverrides.backgroundColor === "string" ? submitOverrides.backgroundColor : isSubmitDisabled ? theme.surfaceColor : theme.primaryColor;

  const handleSubmit = () => {
    if (rating > 0) onSubmit(rating, comment);
  };

  // Same filter web applies: system pills are chrome, a component without data
  // and a message without text have nothing to replay.
  const transcript = useMemo(
    () => (messages ?? []).filter((message) => message.kind !== "system" && (message.componentType ? Boolean(message.componentData) : Boolean(message.message.trim()))),
    [messages],
  );

  const scroller = useRef<ComponentRef<typeof ScrollView>>(null);

  if (transcript.length > 0) {
    return (
      <View testID={ringgId("feedback-screen")} style={styles.transcriptScreen}>
        <Header isCalling isLoading={false} title={title ?? ""} description="" logoUrl={logoUrl} logoStyles={logoStyles} />
        <ScrollView
          testID={ringgId("transcript")}
          ref={scroller}
          style={styles.transcript}
          contentContainerStyle={styles.transcriptList}
          onContentSizeChange={() => scroller.current?.scrollToEnd({ animated: false })}
          showsVerticalScrollIndicator={false}
        >
          {transcript.map((message, index) => {
            if (message.componentType && message.componentData) {
              // Core keeps blocks opaque; the structured contract lives in
              // lib/component-types, hence the cast.
              const componentData = message.componentData as unknown as SimpleComponentPayload | InteractiveFlowPayload;
              if (isBlocks(componentData)) {
                return (
                  <View key={`c_${message.timestamp}__${index}`} testID={ringgId("blocks-widget")} style={styles.replayed}>
                    <BlocksStep config={componentData.data} disabled onAction={async () => {}} />
                  </View>
                );
              }
              return (
                // pointerEvents none is what makes the replay read-only — the
                // flow renders exactly as it did, and answers nothing.
                <View key={`c_${message.timestamp}__${index}`} pointerEvents="none" style={styles.replayed}>
                  <InteractiveFlow initialComponent={componentData} onApiCall={noopApi} />
                </View>
              );
            }
            return <MessageItem key={`${message.timestamp}__${index}`} itemIndex={index} message={message} />;
          })}

          {/* The rating is the transcript's last entry, so it scrolls with it. */}
          <View testID={ringgId("feedback-footer")} style={styles.feedbackFooter}>
            <View testID={ringgId("conversation-ended")} style={styles.endedRow}>
              <View style={[styles.endedRule, { backgroundColor: solidColor(theme.borderColor) }]} />
              <Text style={[styles.endedLabel, { color: theme.mutedTextColor, fontFamily }]}>Conversation ended</Text>
              <View style={[styles.endedRule, { backgroundColor: solidColor(theme.borderColor) }]} />
            </View>
            <Text testID={ringgId("feedback-prompt")} style={[styles.description, { color: theme.mutedTextColor, fontFamily }]}>
              {feedbackScreenConfig?.title ?? "Please rate your experience"}
            </Text>
            <View testID={ringgId("feedback-stars")} style={styles.stars}>
              {stars.map((star) => (
                <StarIcon
                  key={star}
                  filled={star <= rating}
                  filledColor={starFilledColor}
                  emptyColor={starEmptyColor}
                  size={TRANSCRIPT_STAR_SIZE}
                  accessibilityLabel={`Rate ${star} of ${starsCount}`}
                  onPress={() => {
                    if (isSubmitting) return;
                    setRating(star);
                    // No comment box in this variant — the tap IS the rating.
                    onSubmit(star, "");
                  }}
                />
              ))}
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    // iOS keeps the keyboard over the content; Android resizes the window
    // itself, and padding on top of that double-counts the inset.
    <KeyboardAvoidingView testID={ringgId("feedback-screen")} behavior={Platform.OS === "ios" ? "padding" : undefined} style={styles.screen}>
      <View testID={ringgId("feedback-header")} style={styles.header}>
        <Text testID={ringgId("feedback-title")} style={[styles.title, { color: theme.textColor, fontFamily }]}>
          {feedbackScreenConfig?.title ?? `How was your ${callMode === "text" ? "chat" : "call"}?`}
        </Text>
        <Text testID={ringgId("feedback-description")} style={[styles.description, { color: theme.mutedTextColor, fontFamily }]}>
          {feedbackScreenConfig?.description ?? "Your feedback helps us improve!"}
        </Text>
      </View>

      <View testID={ringgId("feedback-stars")} style={styles.stars}>
        {stars.map((star) => (
          <StarIcon
            key={star}
            // Web previews the hovered star; the selection is all touch has.
            filled={star <= rating}
            filledColor={starFilledColor}
            emptyColor={starEmptyColor}
            accessibilityLabel={`Rate ${star} of ${starsCount}`}
            onPress={() => setRating(star)}
          />
        ))}
      </View>

      <TextInput
        testID={ringgId("feedback-comment")}
        value={comment}
        onChangeText={setComment}
        placeholder={feedbackScreenConfig?.placeholder ?? "We'd love to hear more..."}
        placeholderTextColor={PLACEHOLDER_COLOR}
        multiline
        // Android centres multiline text vertically; a textarea starts at the top.
        textAlignVertical="top"
        maxLength={COMMENT_MAX_LENGTH}
        onFocus={() => setIsCommentFocused(true)}
        onBlur={() => setIsCommentFocused(false)}
        style={[
          styles.comment,
          {
            backgroundColor: solidColor(theme.backgroundColor),
            color: theme.textColor,
            // Web's focus ring sits OUTSIDE the border; growing a border here
            // would reflow the box, so the border itself takes the accent.
            borderColor: isCommentFocused ? solidColor(theme.primaryColor) : solidColor(theme.borderColor),
            borderRadius: resolveRadius(theme.borderRadius, COMMENT_RADIUS_FALLBACK),
            fontFamily,
          },
        ]}
      />

      <View testID={ringgId("feedback-actions")} style={styles.actions}>
        {/* Not the shared <Button>: this one is full-width, 44dp, and states
            disabled as an inert surface chip rather than a faded primary. */}
        <Touchable onPress={handleSubmit} disabled={isSubmitDisabled} testID={ringgId("feedback-submit")}>
          <GradientFill color={submitFill} style={[styles.submit, { borderRadius: resolveButtonRadius(theme.buttonStyle) }, submitOverrides]}>
            <Text style={[styles.submitLabel, { color: isSubmitDisabled ? theme.mutedTextColor : theme.primaryTextColor, fontFamily }]} numberOfLines={1}>
              {feedbackScreenConfig?.submitBtnCTA ?? "Submit Feedback"}
            </Text>
          </GradientFill>
        </Touchable>
        <Touchable onPress={onSkip} disabled={isSubmitting} testID={ringgId("feedback-skip")} style={styles.skip}>
          <Text style={[styles.skipLabel, { color: theme.mutedTextColor, fontFamily }]}>Skip</Text>
        </Touchable>
      </View>
    </KeyboardAvoidingView>
  );
};

export default FeedbackScreen;

const styles = StyleSheet.create({
  screen: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24, gap: 16 },

  // Transcript variant. The header floats over the list the same way it does
  // during a live call, so the list clears it with padding rather than layout.
  transcriptScreen: { flex: 1, padding: 8, gap: 8 },
  transcript: { flex: 1 },
  transcriptList: { flexGrow: 1, justifyContent: "flex-end", gap: 12, paddingTop: 48 },
  replayed: { width: "100%" },
  feedbackFooter: { gap: 10, paddingHorizontal: 8, paddingVertical: 8 },
  endedRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  endedRule: { flex: 1, height: StyleSheet.hairlineWidth },
  endedLabel: { fontSize: 13 },
  header: { alignItems: "center" },
  title: { fontSize: 18, lineHeight: 28, fontWeight: "500", textAlign: "center", marginBottom: 4 },
  description: { fontSize: 15, lineHeight: 22, textAlign: "center" },
  stars: { flexDirection: "row", gap: 4, marginVertical: 8 },
  star: { padding: 4 },
  comment: { alignSelf: "stretch", minHeight: 80, padding: 12, fontSize: 15, borderWidth: 1 },
  actions: { alignSelf: "stretch", gap: 4, marginTop: 8 },
  submit: { height: SUBMIT_HEIGHT, alignItems: "center", justifyContent: "center" },
  submitLabel: { fontSize: 15, fontWeight: "500" },
  skip: { paddingVertical: 8, alignItems: "center" },
  skipLabel: { fontSize: 15 },
});
